import { computed, ref, watch } from 'vue';
import { useInternational } from '@/common/locale';
import { QuickFilterItem, QuickFilterSelectedValues, QuickFilterState } from './quickFilter.types';

export interface Props {
  quickFilterResult?: QuickFilterSelectedValues;
  quickFilterList: QuickFilterItem[];
}

export interface Emit {
  (e: 'update:quickFilterResult', result: QuickFilterSelectedValues): void;
}

// QuickFilter 는 'AND' 고정
const LOGICAL_OPERATORS = 'AND' as const;

const initialQuickFilterResult: QuickFilterSelectedValues = {
  logicalOperator: LOGICAL_OPERATORS,
  tokens: [],
};

export const setup = (props: Props, emit: Emit) => {
  const { t } = useInternational();

  const filterResult = computed({
    get: () => props.quickFilterResult ?? initialQuickFilterResult,
    set: (value) => emit('update:quickFilterResult', value),
  });

  const filterStates = ref<Record<string, QuickFilterState>>({});
  const filterCount = computed<number>(() => filterResult.value.tokens.length);

  const initialFilterStates = () => {
    const newStates: Record<string, QuickFilterState> = {};

    props.quickFilterList.forEach((item) => {
      newStates[item.id] = {
        use: false,
        field: item.field,
        filterValue: item.filterValue,
        operator: item.operator,
      };
    });

    filterStates.value = newStates;
    filterResult.value = {
      logicalOperator: LOGICAL_OPERATORS,
      tokens: [],
    };
  };

  const deleteFilterStates = (key: string) => {
    const currentTokens = filterResult.value.tokens.filter((token) => token.id !== key);

    filterResult.value = {
      logicalOperator: LOGICAL_OPERATORS,
      tokens: currentTokens,
    };
  };

  const onClickClear = (key: string) => {
    filterStates.value[key].use = false;

    if (
      Array.isArray(filterStates.value[key].filterValue) &&
      filterStates.value[key].operator === 'between'
    ) {
      filterStates.value[key].filterValue = ['', ''];
    }

    deleteFilterStates(key);
  };

  const setCalendarFilter = (value: [string, string], key: string) => {
    if (value[0] === '' || value[1] === '') return;

    filterStates.value[key] = {
      ...filterStates.value[key],
      use: true,
      filterValue: value,
    };

    const isAlreadyExist = filterResult.value.tokens.findIndex((token) => token.id === key);
    if (isAlreadyExist === -1) {
      const newTokens = [
        ...filterResult.value.tokens,
        {
          id: key,
          key: filterStates.value[key].field,
          operator: filterStates.value[key].operator,
          value,
        },
      ];
      filterResult.value = {
        logicalOperator: LOGICAL_OPERATORS,
        tokens: newTokens,
      };
    }
  };

  const setOneButtonFilter = (key: string) => {
    filterStates.value[key].use = true;

    const newTokens = [
      ...filterResult.value.tokens,
      {
        id: key,
        key: filterStates.value[key].field,
        operator: filterStates.value[key].operator,
        value: filterStates.value[key].filterValue as string,
      },
    ];

    filterResult.value = {
      logicalOperator: LOGICAL_OPERATORS,
      tokens: newTokens,
    };
  };

  const getFilterLabel = (item: QuickFilterItem) => {
    const state = filterStates.value[item.id];
    if (!state || !Array.isArray(state.filterValue)) {
      return '';
    }

    const [fromTime, toTime] = state.filterValue;

    if (item.timeUnit === 'day') {
      const fromDay = fromTime.split(' ')[0] ?? '';
      const toDay = toTime.split(' ')[0] ?? '';

      return `${fromDay} ~ ${toDay}`;
    }

    return `${fromTime} ~ ${toTime}`;
  };

  watch(
    () => props.quickFilterList,
    () => {
      initialFilterStates();
    },
    {
      immediate: true,
    },
  );

  return {
    t,

    filterStates,
    filterCount,

    setCalendarFilter,
    setOneButtonFilter,
    onClickClear,
    getFilterLabel,
  };
};
